import { useMemo, useState, useEffect } from "react";
import { MuiMaterials } from "../../@index.js";
import * as global from "../../data/global";
import { getWindowChromeThemeColor } from "./windowChromeTheme.js";
import { ALL_SITES_OPTION } from "./SiteSelectPhase.js";
import { resolveSiteDisplayName } from "./siteWindowNav.js";
import { ensureSiteInfosLoaded } from "./siteInfosCatalog.js";
import { withScrollHintMenuProps } from "./ScrollHintPaper.js";

/**
 * Header site picker — same visual weight as the current SiteWindowTitle nav button
 * (large, bold, transparent). Use as the first control in Site XXXX headers.
 */
export default function SiteHeaderSiteSelect({
    value = "",
    onChange,
    sites = null,
    disabled = false,
    filterSite = null,
    textSecondary: textSecondaryProp = null,
    includeAllSites = false,
    allSitesLabel = "All Sites",
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [siteInfosLen, setSiteInfosLen] = useState(() => (global.gw?.siteInfos ?? []).length);
    const textSecondary =
        textSecondaryProp ?? getWindowChromeThemeColor("#666666", "#c4a574");
    const menuText = getWindowChromeThemeColor("#333333", "#f4cc8e");
    const menuBg = getWindowChromeThemeColor("#ffffff", "#2d2d2d");
    const menuHover = getWindowChromeThemeColor("#f5f5f5", "#3d3d3d");
    const menuSelected = getWindowChromeThemeColor("#e8f0fe", "#3d3d3d");

    useEffect(() => {
        let cancelled = false;
        ensureSiteInfosLoaded().then(() => {
            if (cancelled) return;
            const n = Array.isArray(sites) && sites.length > 0
                ? sites.length
                : (global.gw?.siteInfos ?? []).length;
            setSiteInfosLen((prev) => (prev === n ? prev : n));
        });
        // Always watch catalog size: parents often pass [] before siteInfos is ready.
        const sync = () => {
            const n = Array.isArray(sites) && sites.length > 0
                ? sites.length
                : (global.gw?.siteInfos ?? []).length;
            setSiteInfosLen((prev) => (prev === n ? prev : n));
        };
        sync();
        const id = setInterval(sync, 250);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [sites]);

    const siteOptions = useMemo(() => {
        const fromProp = Array.isArray(sites) && sites.length > 0 ? sites : null;
        const list = fromProp || (global.gw?.siteInfos ?? []);
        return list
            .filter((s) => {
                if (!s?.siteGuid) return false;
                if (filterSite && !filterSite(s)) return false;
                return true;
            })
            .map((s) => ({
                siteGuid: s.siteGuid,
                siteName: s.siteName || resolveSiteDisplayName(s.siteGuid),
            }))
            .sort((a, b) =>
                String(a.siteName).localeCompare(String(b.siteName), undefined, {
                    sensitivity: "base",
                }),
            );
    }, [sites, filterSite, anchorEl, siteInfosLen, Array.isArray(sites) ? sites.length : 0]);

    const selectedName = useMemo(() => {
        if (!value) return "Select site";
        if (includeAllSites && value === ALL_SITES_OPTION) return allSitesLabel;
        const resolved = resolveSiteDisplayName(value, siteOptions);
        if (resolved && resolved !== "Loading…") return resolved;
        const selected = global.gw?.selectedSite;
        if (
            selected?.siteName
            && global.normalizeGuidForCompare?.(selected.siteGuid)
                === global.normalizeGuidForCompare?.(value)
        ) {
            return selected.siteName;
        }
        return resolved;
    }, [value, siteOptions, includeAllSites, allSitesLabel, siteInfosLen]);

    const open = Boolean(anchorEl);

    return (
        <>
            <MuiMaterials.Button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                }}
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={open ? "true" : undefined}
                aria-controls={open ? "site-header-site-menu" : undefined}
                sx={{
                    flex: "0 1 auto",
                    width: "auto",
                    minWidth: 0,
                    maxWidth: "100%",
                    display: "inline-flex",
                    alignItems: "center",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "1.25rem",
                    lineHeight: 1.6,
                    px: 0.5,
                    py: 0,
                    minHeight: "2.1rem",
                    height: "2.1rem",
                    color: "inherit",
                    backgroundColor: "transparent",
                    justifyContent: "flex-start",
                    alignSelf: "center",
                    "&:hover": {
                        backgroundColor: getWindowChromeThemeColor(
                            "rgba(0,0,0,0.04)",
                            "rgba(255,255,255,0.06)",
                        ),
                    },
                }}
            >
                <MuiMaterials.Typography
                    component="span"
                    sx={{
                        fontWeight: 700,
                        fontSize: "inherit",
                        lineHeight: "inherit",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: value ? "inherit" : textSecondary,
                        fontStyle: value ? "normal" : "italic",
                        display: "block",
                    }}
                >
                    {selectedName}
                </MuiMaterials.Typography>
            </MuiMaterials.Button>
            <MuiMaterials.Menu
                id="site-header-site-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                style={{ zIndex: 1600 }}
                sx={{ zIndex: 1600 }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                {...withScrollHintMenuProps({
                    PaperProps: {
                        sx: {
                            backgroundColor: menuBg,
                            backgroundImage: "none",
                            color: menuText,
                            minWidth: 280,
                            maxWidth: "min(90vw, 480px)",
                        },
                    },
                    MenuListProps: {
                        sx: { backgroundColor: menuBg },
                    },
                })}
            >
                {includeAllSites ? (
                    <MuiMaterials.MenuItem
                        selected={value === ALL_SITES_OPTION}
                        onClick={() => {
                            setAnchorEl(null);
                            if (value !== ALL_SITES_OPTION) onChange?.(ALL_SITES_OPTION);
                        }}
                        sx={{
                            fontWeight: 600,
                            color: `${menuText} !important`,
                            backgroundColor: "transparent",
                            "&.Mui-selected": {
                                backgroundColor: `${menuSelected} !important`,
                                color: `${menuText} !important`,
                            },
                            "&:hover": {
                                backgroundColor: `${menuHover} !important`,
                                color: `${menuText} !important`,
                            },
                        }}
                    >
                        {allSitesLabel}
                    </MuiMaterials.MenuItem>
                ) : null}
                {siteOptions.length === 0 ? (
                    <MuiMaterials.MenuItem disabled sx={{ color: `${menuText} !important` }}>
                        No sites
                    </MuiMaterials.MenuItem>
                ) : (
                    siteOptions.map((s) => {
                        const selected =
                            global.normalizeGuidForCompare(s.siteGuid) ===
                            global.normalizeGuidForCompare(value);
                        return (
                            <MuiMaterials.MenuItem
                                key={s.siteGuid}
                                selected={selected}
                                onClick={() => {
                                    setAnchorEl(null);
                                    if (!selected) onChange?.(s.siteGuid);
                                }}
                                sx={{
                                    color: `${menuText} !important`,
                                    backgroundColor: "transparent",
                                    "&.Mui-selected": {
                                        backgroundColor: `${menuSelected} !important`,
                                        color: `${menuText} !important`,
                                    },
                                    "&:hover": {
                                        backgroundColor: `${menuHover} !important`,
                                        color: `${menuText} !important`,
                                    },
                                }}
                            >
                                {s.siteName}
                            </MuiMaterials.MenuItem>
                        );
                    })
                )}
            </MuiMaterials.Menu>
        </>
    );
}
